import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, authFetch } from '../../auth';
import { useView } from '../../shells/ViewContext';
import HopDateTimePicker from '../shared/HopDateTimePicker';

const ROLES = ['employee', 'front_desk', 'admin'];

const fmt     = (v) => v ?? '—';
const fmtRate = (r) => r ? `$${parseFloat(r).toFixed(2)}/hr` : '—';

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

const fmtEntryDate = (iso) =>
  new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

// Sprint 13.6: visual overnight indicator. If clock-in and clock-out
// fall on different *local* days, render the row's date as
// "Mon, May 25 → Tue, May 26" so the operator can see at a glance
// that the entry spans midnight (image #19 only showed the
// clock-in day, which read as if the whole shift happened Monday).
const isOvernight = (inIso, outIso) => {
  if (!inIso || !outIso) return false;
  const a = new Date(inIso);
  const b = new Date(outIso);
  return a.getFullYear() !== b.getFullYear()
      || a.getMonth()    !== b.getMonth()
      || a.getDate()     !== b.getDate();
};
const fmtEntryDateRange = (inIso, outIso) => (
  isOvernight(inIso, outIso)
    ? `${fmtEntryDate(inIso)} → ${fmtEntryDate(outIso)}`
    : fmtEntryDate(inIso)
);

const hoursOf = (iso1, iso2) => {
  const start = new Date(iso1);
  const end   = iso2 ? new Date(iso2) : new Date();
  return (end - start) / 3600000;
};

const fmtHrs = (h) => {
  if (!h || isNaN(h)) return '—';
  const hours = Math.floor(h);
  const mins  = Math.round((h - hours) * 60);
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours)         return `${hours}h`;
  return `${mins}m`;
};

// Sprint 19.2: "(425) 377-5167" for 10-digit phones; anything else as-is.
const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : fmt(p);
};
const fmtDateShort = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Sprint 19.2: week math for the Time Entries navigator. Weeks start on
// MONDAY (same as the Performance trend bars / server weekStart), in the
// admin's local timezone. weekOffset 0 = this week, -1 = last week, …
const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// Math.round absorbs the 23h/25h weeks around DST changes.
const weekOffsetOf = (d) =>
  Math.round((mondayOf(d) - mondayOf(new Date())) / (7 * 86400000));
const fmtMD = (d) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });

// Convert ISO timestamp to value usable by datetime-local input (YYYY-MM-DDTHH:MM)
// in the user's local timezone.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Sprint 11.2.1: `userId` comes from view params (set by StaffManager
// when an admin clicks a row), not from the URL. Back goes through
// the AdminShell's `goTo('staff')` instead of a URL navigation.
// Sprint 18.13: `editEntryId` is passed by AdminHome when the admin
// clicks "Edit hours" on the Past-scheduled-end panel. We open the
// entry-edit modal for that specific entry as soon as `entries` is
// loaded, once per mount (a hasAutoOpenedRef gate prevents the
// modal from re-opening after the user dismisses it).
const StaffDetail = ({ userId, editEntryId, mode }) => {
  const isDev = mode === 'dev';   // Sprint 24.4: the full Dev view only (a dev viewing "as Manager" sees exactly what a manager sees)
  const { goTo } = useView();

  const [emp,           setEmp]           = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [departments,   setDepartments]   = useState([]);
  const [editing,       setEditing]       = useState(false);
  const [form,          setForm]          = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [toggling,      setToggling]      = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error,         setError]         = useState('');
  const [pinBusy,       setPinBusy]       = useState(false);
  const [pinErr,        setPinErr]        = useState('');

  // Time entries (Sprint 5D)
  const [entries,    setEntries]    = useState([]);
  const [entryLoad,  setEntryLoad]  = useState(true);
  // Sprint 19.2: which week the entries list shows (0 = this week).
  const [weekOffset, setWeekOffset] = useState(0);
  const [editEntry,  setEditEntry]  = useState(null);   // entry object being edited
  const [entryForm,  setEntryForm]  = useState({ in: '', out: '' });
  const [entrySave,  setEntrySave]  = useState(false);
  const [entryErr,   setEntryErr]   = useState('');
  // Sprint 18.11 — separate create flow from edit flow. `entryMode`
  // drives the modal title + submit handler; conflict carries the
  // overlapping row returned by the server's 409 so we can show the
  // admin *which* punch conflicts (not just "an overlap exists").
  const [entryMode,     setEntryMode]     = useState('edit'); // 'edit' | 'create'
  const [entryConflict, setEntryConflict] = useState(null);
  // Sprint 19.3: two-step delete inside the edit modal.
  const [confirmEntryDelete, setConfirmEntryDelete] = useState(false);
  const [entryDeleting,      setEntryDeleting]      = useState(false);

  // Performance dashboard (Sprint 6D)
  const [perf,       setPerf]       = useState(null);
  const [perfLoad,   setPerfLoad]   = useState(true);
  const [perfErr,    setPerfErr]    = useState('');
  const [period,     setPeriod]     = useState('week'); // 'week' | 'month' | 'year'

  // OT bulk approve (Sprint 6.2D)
  const [otBusy,     setOtBusy]     = useState(false);
  const [otMsg,      setOtMsg]      = useState('');

  const reloadEmployee = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await apiFetch(`/admin/employees/${userId}`);
    if (ok && data?.success) setEmp(data.employee);
    setLoading(false);
  }, [userId]);

  const reloadEntries = useCallback(async () => {
    setEntryLoad(true);
    const res  = await authFetch(`/api/admin/employees/${userId}/time-entries`);
    const data = await res.json();
    if (data.success) setEntries(data.timeEntries);
    setEntryLoad(false);
  }, [userId]);

  useEffect(() => {
    reloadEmployee();
    reloadEntries();
    authFetch('/api/admin/departments').then(r => r.json()).then(d => {
      if (d.success) setDepartments(d.departments);
    });
  }, [reloadEmployee, reloadEntries]);

  // Performance fetch (re-runs when period changes)
  const reloadPerf = useCallback(async () => {
    setPerfLoad(true);
    setPerfErr('');
    // Sprint 13.6: pass the operator's local TZ offset so the
    // performance window aligns to the *operator's* week/month/year.
    const tzOff = new Date().getTimezoneOffset();
    const { ok, status, data } = await apiFetch(`/admin/staff/${userId}/performance?period=${period}&tz_offset_minutes=${tzOff}`);
    if (ok && data?.success) {
      setPerf(data);
    } else {
      setPerfErr(data?.message || `Could not load performance${status ? ` (HTTP ${status})` : ''}.`);
    }
    setPerfLoad(false);
  }, [userId, period]);

  useEffect(() => {
    let active = true;
    reloadPerf().then(() => { if (!active) return; });
    return () => { active = false; };
  }, [reloadPerf]);

  // Bulk-approve all OT in the displayed period.
  const approveOT = async () => {
    setOtBusy(true);
    setOtMsg('');
    // Sprint 13.6: same TZ alignment as performance fetch above.
    const tzOff = new Date().getTimezoneOffset();
    const { ok, data } = await apiFetch(`/admin/staff/${userId}/approve-ot?period=${period}&tz_offset_minutes=${tzOff}`, {
      method: 'POST',
    });
    setOtBusy(false);
    if (ok && data?.success) {
      setOtMsg(`Approved ${data.approvedCount} ${data.approvedCount === 1 ? 'entry' : 'entries'}.`);
      setTimeout(() => setOtMsg(''), 3000);
      reloadPerf();
    } else {
      setOtMsg(data?.message || 'Could not approve');
    }
  };

  // ── profile edit ──────────────────────────────────────────────────────────
  const startEdit = () => {
    setForm({
      name:           emp.name,
      phone:          emp.phone_number  || '',
      username:       emp.username      || '',
      employeeCode:   emp.employee_code || '',
      birthday:       emp.birthday ? emp.birthday.split('T')[0] : '',
      role:           emp.role,
      departmentId:   emp.department_id || '',
      hireDate:       emp.hire_date ? emp.hire_date.split('T')[0] : '',
      baseHourlyRate: emp.base_hourly_rate || '',
      preferredLanguage: emp.preferred_language || 'en',
    });
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    // Sprint 9.1: birthday counts as a first-class identifier here too.
    if (!form.phone && !form.username && !form.employeeCode && !form.birthday) {
      setError('Provide at least one of phone, username, employee ID, or birthday');
      return;
    }
    setSaving(true);
    setError('');
    const res  = await authFetch(`/api/admin/employees/${emp.user_id}`, {
      method:  'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:           form.name,
        phoneNumber:    form.phone        || null,
        username:       form.username     || null,
        employeeCode:   form.employeeCode || null,
        birthday:       form.birthday     || null,
        role:           form.role,
        hireDate:       form.hireDate,
        departmentId:   form.departmentId || null,
        baseHourlyRate: form.baseHourlyRate || null,
        preferredLanguage: form.preferredLanguage || 'en',
      }),
    });
    const data = await res.json();
    setSaving(false);
    if (data.success) {
      setEmp(prev => ({ ...prev, ...data.employee }));
      setEditing(false);
    } else {
      setError(data.message);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    setError('');
    const res  = await authFetch(`/api/admin/employees/${emp.user_id}/status`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !emp.active }),
    });
    const data = await res.json();
    setToggling(false);
    if (data.success) {
      setEmp(prev => ({ ...prev, active: !prev.active }));
      setConfirmDelete(false);
    } else {
      setError(data.message);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    const res  = await authFetch(`/api/admin/employees/${emp.user_id}`, { method: 'DELETE' });
    const data = await res.json();
    setDeleting(false);
    if (data.success) goTo('staff');
    else              setError(data.message);
  };

  // ── PIN ───────────────────────────────────────────────────────────────────
  const togglePinRequired = async () => {
    setPinBusy(true);
    setPinErr('');
    const { ok, data } = await apiFetch(`/admin/employees/${emp.user_id}/pin`, {
      method: 'PATCH',
      body:   JSON.stringify({ pin_required: !emp.pin_required }),
    });
    setPinBusy(false);
    if (ok && data?.success) setEmp(prev => ({ ...prev, ...data.employee }));
    else                     setPinErr(data?.message || 'Could not update PIN setting');
  };

  const resetPin = async () => {
    if (!window.confirm(`Reset PIN for ${emp.name}? They'll be prompted to set a new one on their next login.`)) return;
    setPinBusy(true);
    setPinErr('');
    const { ok, data } = await apiFetch(`/admin/employees/${emp.user_id}/pin/reset`, {
      method: 'POST',
    });
    setPinBusy(false);
    if (ok && data?.success) setEmp(prev => ({ ...prev, ...data.employee }));
    else                     setPinErr(data?.message || 'Could not reset PIN');
  };

  // Sprint 24.4 — dev only: show the PIN (decrypted from the server's encrypted copy; every reveal is audit-logged). Auto-hides after 20 s.
  const [revealed, setRevealed] = useState(null);   // { pin } | { why }
  useEffect(() => { setRevealed(null); }, [userId]);
  useEffect(() => { if (!revealed || !revealed.pin) return undefined; const id = setTimeout(() => setRevealed(null), 20000); return () => clearTimeout(id); }, [revealed]);
  const REVEAL_WHY = {
    not_configured: 'Not available: PIN_VIEW_KEY is not set on the server.',
    not_stored: 'Not viewable yet — it becomes viewable after the person signs in with their PIN or changes it.',
    no_pin: 'No PIN is set.',
  };
  const revealPin = async () => {
    setPinBusy(true); setPinErr('');
    const { ok, data } = await apiFetch(`/dev/employees/${emp.user_id}/pin/reveal`, { method: 'POST' });
    setPinBusy(false);
    if (ok && data?.success) setRevealed(data.pin ? { pin: data.pin } : { why: REVEAL_WHY[data.reason] || 'Not viewable.' });
    else setPinErr(data?.message || 'Could not reveal the PIN');
  };

  // ── Hour override ─────────────────────────────────────────────────────────
  const openEntryEdit = (entry) => {
    setEntryMode('edit');
    setEditEntry(entry);
    setEntryForm({
      in:  toLocalInput(entry.clock_in_time),
      out: toLocalInput(entry.clock_out_time),
    });
    setEntryErr('');
    setEntryConflict(null);
    setConfirmEntryDelete(false);
  };

  // Sprint 18.13 — auto-open modal on the entry AdminHome sent us to.
  // Guard with a ref so dismissing the modal doesn't re-trigger the
  // effect on the next render. `editEntryId` comes from view.params
  // via the AdminShell prop spread; entries load async so we wait
  // for the load to finish + the entry to be present.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    if (!editEntryId) return;
    if (entryLoad) return;
    const entry = entries.find(e => e.entry_id === editEntryId);
    if (!entry) return;
    hasAutoOpenedRef.current = true;
    // The entry may be in a past week — show that week behind the modal.
    setWeekOffset(weekOffsetOf(entry.clock_in_time));
    openEntryEdit(entry);
  }, [editEntryId, entryLoad, entries]);

  // Sprint 18.11 — open the same modal in "create" mode. The form
  // starts blank; submit POSTs to the new create endpoint. Using a
  // sentinel object (rather than `null`) so the modal's render
  // guard `{editEntry && ...}` still fires.
  const openEntryCreate = () => {
    setEntryMode('create');
    setEditEntry({ entry_id: null });
    setEntryForm({ in: '', out: '' });
    setEntryErr('');
    setEntryConflict(null);
  };

  const closeEntryEdit = () => {
    setConfirmEntryDelete(false);
    setEditEntry(null);
    setEntryErr('');
    setEntryConflict(null);
  };

  // Sprint 19.3: delete the whole entry (audit-logged server-side).
  const deleteEntry = async () => {
    if (!editEntry?.entry_id) return;
    setEntryDeleting(true);
    setEntryErr('');
    const { ok, data } = await apiFetch(`/admin/time-entries/${editEntry.entry_id}`, { method: 'DELETE' });
    setEntryDeleting(false);
    if (ok && data?.success) {
      closeEntryEdit();
      reloadEntries();
    } else {
      setConfirmEntryDelete(false);
      setEntryErr(data?.message || 'Could not delete entry');
    }
  };

  const saveEntryEdit = async (e) => {
    e.preventDefault();
    setEntrySave(true);
    setEntryErr('');
    setEntryConflict(null);
    // datetime-local gives a string like "2026-04-29T08:30" interpreted as
    // local time. Build a Date from it and send ISO (UTC) to the server.
    const inDate  = entryForm.in ? new Date(entryForm.in) : null;
    const outDate = entryForm.out ? new Date(entryForm.out) : null;
    if (!inDate || isNaN(inDate)) {
      setEntrySave(false);
      setEntryErr('Clock-in is required');
      return;
    }
    if (outDate && outDate <= inDate) {
      setEntrySave(false);
      setEntryErr('Clock-out must be after clock-in');
      return;
    }

    const body = JSON.stringify({
      clock_in_time:  inDate.toISOString(),
      clock_out_time: outDate ? outDate.toISOString() : null,
    });
    const { ok, data } = entryMode === 'create'
      ? await apiFetch(`/admin/employees/${userId}/time-entries`, { method: 'POST',  body })
      : await apiFetch(`/admin/time-entries/${editEntry.entry_id}`,  { method: 'PATCH', body });
    setEntrySave(false);
    if (ok && data?.success) {
      closeEntryEdit();
      // Follow the saved entry so an edit/add never "disappears" into
      // a week the admin isn't looking at.
      setWeekOffset(weekOffsetOf(inDate));
      reloadEntries();
    } else {
      // 409 carries `conflict` (the colliding entry). Render the
      // overlapping window so the admin can see what's in the way
      // and either edit that one instead or pick a different time.
      if (data?.code === 'overlap' && data?.conflict) {
        setEntryConflict(data.conflict);
      }
      setEntryErr(data?.message || 'Could not save');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return <div className="emp-detail"><div className="emp-loading">Loading…</div></div>;
  if (!emp)    return (
    <div className="emp-detail">
      <div className="emp-detail-topbar">
        <button className="btn-back" onClick={() => goTo('staff')}>‹ Staff</button>
      </div>
      <div className="emp-empty">Staff not found.</div>
    </div>
  );

  const deptName = () =>
    departments.find(d => d.department_id === emp.department_id)?.name || emp.department || '—';

  // Sprint 19.2: entries for the selected week (Mon 00:00 → next Mon
  // 00:00, bucketed by clock-in so an overnight shift stays in the week
  // it started — same rule as the Performance trend). `entries` is
  // already loaded for all time, so switching weeks is instant and
  // costs no extra DB queries.
  const weekStart  = addDays(mondayOf(new Date()), weekOffset * 7);
  const weekEnd    = addDays(weekStart, 7);
  const weekLast   = addDays(weekStart, 6);
  const weekEntries = entries.filter(e => {
    const t = new Date(e.clock_in_time);
    return t >= weekStart && t < weekEnd;
  });
  const weekHours = weekEntries.reduce(
    (sum, e) => sum + hoursOf(e.clock_in_time, e.clock_out_time), 0);
  const hasOlder = entries.some(e => new Date(e.clock_in_time) < weekStart);
  const weekTitle = weekOffset === 0 ? 'This week'
    : weekOffset === -1 ? 'Last week'
    : `${-weekOffset} weeks ago`;

  // Profile facts shown beside the name (replaces the old full-width
  // 8-row info table).
  const facts = [
    ['Phone',       fmtPhone(emp.phone_number)],
    ['Username',    fmt(emp.username)],
    ['Employee ID', fmt(emp.employee_code)],
    ['Birthday',    fmtDateShort(emp.birthday)],
    ['Role',        emp.role.replace('_', ' '), 'is-cap'],
    ['Department',  deptName()],
    ['Hire date',   fmtDateShort(emp.hire_date)],
    ['Hourly rate', fmtRate(emp.base_hourly_rate)],
  ];

  return (
    <div className="emp-detail">
      {/* Header */}
      <div className="emp-detail-topbar">
        <button className="btn-back" onClick={() => goTo('staff')}>‹ Staff</button>
        {!editing && <button className="btn-edit" onClick={startEdit}>Edit</button>}
      </div>

      {/* Profile card */}
      <div className="emp-detail-profile">
        <div className="emp-detail-avatar">{emp.name.charAt(0).toUpperCase()}</div>
        <div className="emp-detail-id">
          <div className={`emp-detail-namerow${!editing ? ' has-facts' : ''}`}>
            <div className="emp-detail-name">{emp.name}</div>
            <span className={`emp-badge ${emp.active ? 'badge-active' : 'badge-inactive'}`}>
              {emp.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {/* Sprint 19.2: facts live beside the name, not in a separate table. */}
          {!editing && (
            <dl className="emp-detail-facts">
              {facts.map(([label, value, cls]) => (
                <div key={label} className="emp-fact">
                  <dt>{label}</dt>
                  <dd className={`${cls || ''}${value === '—' ? ' is-empty' : ''}`}>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      {error && <div className="admin-error" style={{ margin: '0 0 16px' }}>{error}</div>}

      {/* ── Performance dashboard (Sprint 6D) ─────────────────────────────── */}
      {!editing && (
        <section className="staff-perf">
          <div className="staff-perf-head">
            <h3 className="staff-perf-title">Performance</h3>
            <div className="staff-perf-tabs">
              {['week', 'month', 'year'].map(p => (
                <button
                  key={p}
                  type="button"
                  className={`staff-perf-tab${period === p ? ' is-active' : ''}`}
                  onClick={() => setPeriod(p)}
                >
                  {p[0].toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {perfErr && <div className="admin-error" style={{ margin: '0 0 12px' }}>{perfErr}</div>}

          <div className="staff-perf-stats">
            {/* Hours worked */}
            <div className="staff-perf-card">
              <div className="staff-perf-eyebrow">Hours worked</div>
              <div className="staff-perf-num">
                {perfLoad ? '—' : `${perf?.hoursWorked ?? 0}h`}
              </div>
              {perf?.comparison?.deltaPct != null ? (
                <div className={`staff-perf-delta ${perf.comparison.deltaPct >= 0 ? 'is-up' : 'is-down'}`}>
                  {perf.comparison.deltaPct >= 0 ? '↑' : '↓'} {Math.abs(Math.round(perf.comparison.deltaPct * 100))}%
                  <span className="staff-perf-delta-label">{perf.comparison.label}</span>
                </div>
              ) : (
                <div className="staff-perf-meta">
                  {perf?.comparison?.previousValue
                    ? `${perf.comparison.previousValue}h previous period`
                    : 'no prior data'}
                </div>
              )}
            </div>

            {/* On-time rate */}
            <div className="staff-perf-card">
              <div className="staff-perf-eyebrow">On-time rate</div>
              <div className="staff-perf-num">
                {perfLoad ? '—' : (perf?.onTimeRate != null ? `${Math.round(perf.onTimeRate * 100)}%` : '—')}
              </div>
              <div className="staff-perf-meta">
                {perf?.shiftsWorked
                  ? `${perf.shiftsOnTime || 0} of ${perf.shiftsWorked} on time`
                  : 'no shifts worked'}
              </div>
            </div>

            {/* Overtime */}
            <div className="staff-perf-card">
              <div className="staff-perf-eyebrow">Overtime</div>
              <div className={`staff-perf-num ${(perf?.hoursOvertimePending || 0) > 0 ? 'is-warn' : ''}`}>
                {perfLoad ? '—' : `${perf?.hoursOvertime ?? 0}h`}
              </div>
              <div className="staff-perf-meta">
                past {perf?.config?.overtime_threshold_hours ?? 40}h/wk
              </div>
              {(perf?.hoursOvertime ?? 0) > 0 && (
                <div className="staff-perf-ot-split">
                  <span className="staff-perf-ot-pending">
                    {perf.hoursOvertimePending}h pending
                  </span>
                  <span className="staff-perf-ot-dot">·</span>
                  <span className="staff-perf-ot-approved">
                    {perf.hoursOvertimeApproved}h approved
                  </span>
                </div>
              )}
              {(perf?.hoursOvertimePending ?? 0) > 0 && (
                <button
                  type="button"
                  className="staff-perf-ot-btn"
                  onClick={approveOT}
                  disabled={otBusy}
                >
                  {otBusy ? 'Approving…' : 'Approve OT'}
                </button>
              )}
              {otMsg && <div className="staff-perf-ot-msg">{otMsg}</div>}
            </div>

            {/* Shifts */}
            <div className="staff-perf-card">
              <div className="staff-perf-eyebrow">Shifts</div>
              <div className="staff-perf-num">
                {perfLoad ? '—' : (perf?.shiftsWorked ?? 0)}
              </div>
              <div className="staff-perf-meta">
                {perf
                  ? `${perf.shiftsMissed || 0} missed · ${perf.shiftsLate || 0} late`
                  : '—'}
              </div>
            </div>
          </div>

          {/* 8-week trend chart */}
          <div className="staff-perf-trend">
            <div className="staff-perf-trend-head">
              <span className="staff-perf-trend-title">Last 8 weeks</span>
              <span className="staff-perf-trend-sub">weekly hours</span>
            </div>
            <div className="staff-perf-bars">
              {(perf?.trend || Array.from({ length: 8 }, () => ({ hours: 0 }))).map((t, i) => {
                const max = Math.max(8, ...((perf?.trend || []).map(x => x.hours)));
                const pct = max > 0 ? (t.hours / max) * 100 : 0;
                const isOT = t.hours > (perf?.config?.overtime_threshold_hours || 40);
                const barOffset = t.weekStart ? weekOffsetOf(t.weekStart + 'T00:00:00') : null;
                const selected  = barOffset !== null && barOffset === weekOffset;
                return (
                  <button
                    type="button"
                    key={t.weekStart || i}
                    className={`staff-perf-bar-col${selected ? ' is-selected' : ''}`}
                    disabled={barOffset === null}
                    onClick={() => setWeekOffset(barOffset)}
                    title={t.weekStart ? 'Show this week\'s time entries' : undefined}
                    aria-pressed={selected}
                  >
                    <div className="staff-perf-bar-track">
                      <div
                        className={`staff-perf-bar-fill${isOT ? ' is-overtime' : ''}`}
                        style={{ height: `${pct}%`, opacity: t.hours ? 1 : 0.2 }}
                      />
                    </div>
                    <div className="staff-perf-bar-hours">{t.hours ? `${t.hours}h` : ''}</div>
                    <div className="staff-perf-bar-label">
                      {t.weekStart
                        ? new Date(t.weekStart + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })
                        : ''}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Edit form */}
      {editing ? (
        <form className="add-form" onSubmit={handleSave}>
          <div className="add-form-grid">
            <div className="admin-field">
              <label>Full Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="admin-field">
              <label>Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
              </select>
            </div>
            <div className="admin-field">
              <label>Department</label>
              <select value={form.departmentId} onChange={e => setForm(f => ({ ...f, departmentId: e.target.value }))}>
                <option value="">— None —</option>
                {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
              </select>
            </div>
            <div className="admin-field">
              <label>Hire Date *</label>
              <input type="date" value={form.hireDate} onChange={e => setForm(f => ({ ...f, hireDate: e.target.value }))} required />
            </div>
            <div className="admin-field">
              <label>Hourly Rate ($)</label>
              <input type="number" step="0.01" min="0" value={form.baseHourlyRate} onChange={e => setForm(f => ({ ...f, baseHourlyRate: e.target.value }))} placeholder="0.00" />
            </div>
            {/* Sprint 16.2: per-staff UI language. Drives every
                staff-facing screen post-login. */}
            <div className="admin-field">
              <label>Preferred language</label>
              <select
                value={form.preferredLanguage || 'en'}
                onChange={e => setForm(f => ({ ...f, preferredLanguage: e.target.value }))}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="zh">中文</option>
              </select>
            </div>
            <div className="admin-field add-form-section">
              <div className="add-form-section-label">Login identifiers — at least one required</div>
              <div className="add-form-section-sub">Clear a field to remove that login method.</div>
            </div>
            <div className="admin-field">
              <label>Phone Number</label>
              <input
                inputMode="numeric"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g,'').slice(0,10) }))}
                placeholder="10 digits"
              />
            </div>
            <div className="admin-field">
              <label>Username</label>
              <input
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/[^A-Za-z0-9._-]/g,'').slice(0,16) }))}
                placeholder="3–16 chars · letters/numbers/._-"
              />
            </div>
            <div className="admin-field">
              <label>Employee ID</label>
              <input
                inputMode="numeric"
                value={form.employeeCode}
                onChange={e => setForm(f => ({ ...f, employeeCode: e.target.value.replace(/\D/g,'').slice(0,6) }))}
                placeholder="4–6 digits"
              />
            </div>
            <div className="admin-field">
              <label>Birthday</label>
              <input
                type="date"
                value={form.birthday}
                onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))}
              />
              <span className="admin-field-hint">
                Optional. Staff can sign in by entering MMDDYYYY at the keypad if the property allows the birthday login method.
              </span>
            </div>
          </div>
          <div className="add-form-actions" style={{ gap: '10px' }}>
            <button type="button" className="btn-logout" onClick={cancelEdit}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      ) : null}

      {/* Time entries — Sprint 5D (moved above PIN Access in 19.3) */}
      {!editing && (
        <div className="emp-pin-section">
          <div className="emp-entries-head">
            <h3 className="emp-pin-title">Time Entries</h3>
            {/* Sprint 18.11 — manual-add affordance for missed
                shifts. Opens the same modal as Edit, but in create
                mode (blank fields, POSTs a new row). */}
            <button
              type="button"
              className="emp-entry-add"
              onClick={openEntryCreate}
              title="Add a manual time entry"
            >
              + Add entry
            </button>
          </div>

          {/* Sprint 19.2 — week navigator. Default = this week; ‹ › step
              through weeks, and tapping a bar in "Last 8 weeks" above
              jumps straight to that week. */}
          <div className="emp-week-nav">
            <button
              type="button"
              className="emp-week-step"
              onClick={() => setWeekOffset(o => o - 1)}
              disabled={!hasOlder}
              aria-label="Previous week"
            >‹</button>
            <div className="emp-week-label">
              <div className="emp-week-range">{fmtMD(weekStart)} – {fmtMD(weekLast)}</div>
              <div className="emp-week-sub">
                {weekTitle}
                {!entryLoad && ` · ${fmtHrs(weekHours) === '—' ? '0h' : fmtHrs(weekHours)} · ${weekEntries.length} ${weekEntries.length === 1 ? 'entry' : 'entries'}`}
              </div>
            </div>
            <button
              type="button"
              className="emp-week-step"
              onClick={() => setWeekOffset(o => Math.min(0, o + 1))}
              disabled={weekOffset >= 0}
              aria-label="Next week"
            >›</button>
            {weekOffset !== 0 && (
              <button type="button" className="emp-week-today" onClick={() => setWeekOffset(0)}>
                This week
              </button>
            )}
          </div>

          {entryLoad && <div className="emp-pin-meta">Loading…</div>}
          {!entryLoad && weekEntries.length === 0 && (
            <div className="emp-pin-meta">
              {entries.length === 0 ? 'No time entries yet.' : 'No time entries this week.'}
            </div>
          )}

          {!entryLoad && weekEntries.length > 0 && (
            <ul className="emp-entries-list">
              {weekEntries.map(e => {
                const hrs = e.clock_out_time ? hoursOf(e.clock_in_time, e.clock_out_time) : null;
                return (
                  <li key={e.entry_id} className={`emp-entry-row${e.system_generated ? ' is-system' : ''}`}>
                    <div className="emp-entry-main">
                      <div className="emp-entry-date">
                        {fmtEntryDateRange(e.clock_in_time, e.clock_out_time)}
                        {/* Sprint 16.4: badge on auto-closed entries
                            so the admin can spot them at a glance
                            and adjust upward if the staff actually
                            worked past their scheduled end. */}
                        {e.system_generated && (
                          <span
                            className="emp-entry-system-badge"
                            title="Auto-closed by the system because the staff didn't clock out before the grace window expired. Hours reflect the scheduled end. Edit if they worked later."
                          >AUTO</span>
                        )}
                      </div>
                      <div className="emp-entry-times">
                        {fmtTime(e.clock_in_time)} → {e.clock_out_time
                          ? fmtTime(e.clock_out_time)
                          : <span className="emp-entry-open">in progress</span>}
                      </div>
                    </div>
                    <div className="emp-entry-hours">{e.clock_out_time ? fmtHrs(hrs) : '—'}</div>
                    <button
                      className="emp-entry-edit"
                      onClick={() => openEntryEdit(e)}
                      title="Override hours"
                    >
                      Edit
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* PIN management */}
      {!editing && (
        <div className="emp-pin-section">
          <h3 className="emp-pin-title">PIN Access</h3>

          <div className="emp-pin-row">
            <div className="emp-pin-info">
              <div className="emp-pin-label">Require PIN at sign-in</div>
              <div className="emp-pin-meta">
                {emp.pin_required
                  ? 'Employee must enter their PIN to log in.'
                  : 'Employee can log in with their identifier alone.'}
              </div>
            </div>
            <button
              className={`emp-pin-toggle ${emp.pin_required ? 'is-on' : ''}`}
              onClick={togglePinRequired}
              disabled={pinBusy}
              aria-label="Toggle PIN required"
            />
          </div>

          <div className="emp-pin-row">
            <div className="emp-pin-info">
              <div className="emp-pin-label">PIN status</div>
              <div className="emp-pin-meta">
                {emp.pin_must_set
                  ? 'Reset pending — employee will set a new PIN at next login.'
                  : emp.has_pin
                    ? (isDev ? 'PIN is set. As dev you can reveal it; managers can only reset it.' : 'PIN is set. You can reset it but cannot view it.')
                    : 'No PIN set yet.'}
              </div>
              {isDev && revealed && (revealed.pin
                ? <div className="emp-pin-reveal" role="status" aria-live="polite">PIN <code>{revealed.pin}</code> <small>hides in 20 s</small> <button type="button" className="emp-pin-hide" onClick={() => setRevealed(null)}>Hide</button></div>
                : <div className="emp-pin-meta emp-pin-why" role="status">{revealed.why}</div>)}
            </div>
            <div className="emp-pin-actions">
              {isDev && emp.has_pin && !emp.pin_must_set && !(revealed && revealed.pin) && (
                <button className="btn-pin-reset" onClick={revealPin} disabled={pinBusy}>{pinBusy ? '…' : 'Reveal PIN'}</button>
              )}
              <button className="btn-pin-reset" onClick={resetPin} disabled={pinBusy}>
                {pinBusy ? '…' : 'Reset PIN'}
              </button>
            </div>
          </div>

          {pinErr && <div className="admin-error" style={{ marginTop: 8 }}>{pinErr}</div>}
        </div>
      )}

      {/* Status + Delete actions */}
      {!editing && (
        <div className="emp-detail-actions">
          <button
            className={`btn-toggle-full ${emp.active ? 'btn-deactivate' : 'btn-activate'}`}
            onClick={handleToggle}
            disabled={toggling}
          >
            {toggling ? '…' : emp.active ? 'Deactivate Employee' : 'Activate Employee'}
          </button>

          {!emp.active && (
            confirmDelete ? (
              <div className="delete-confirm">
                <span>Permanently delete <strong>{emp.name}</strong>?</span>
                <div className="delete-confirm-btns">
                  <button className="btn-logout" onClick={() => setConfirmDelete(false)}>Cancel</button>
                  <button className="btn-delete-confirm" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn-delete" onClick={() => setConfirmDelete(true)}>
                Delete Employee
              </button>
            )
          )}
        </div>
      )}

      {/* Entry edit/create modal — Sprint 18.11 added create mode */}
      {editEntry && (
        <div className="entry-edit-overlay" onClick={closeEntryEdit}>
          <form className="entry-edit-modal" onClick={e => e.stopPropagation()} onSubmit={saveEntryEdit}>
            <div className="entry-edit-header">
              <h3>{entryMode === 'create' ? 'Add Time Entry' : 'Override Hours'}</h3>
              <button type="button" className="entry-edit-close" onClick={closeEntryEdit}>✕</button>
            </div>
            <p className="entry-edit-sub">
              {entryMode === 'create'
                ? 'Manually record a shift the staff missed clocking in or out for. The new entry is checked against existing punches to prevent overlaps.'
                : 'Edit clock-in / clock-out for this shift. The change is logged to the audit trail.'}
            </p>
            <div className="entry-edit-fields">
              <label>
                <span>Clock In</span>
                {/* Sprint 18.12 — swapped native datetime-local for
                    the themed HopDateTimePicker. Same string shape
                    on the wire, just a custom UI. */}
                <HopDateTimePicker
                  value={entryForm.in}
                  onChange={(v) => setEntryForm(f => ({ ...f, in: v }))}
                  required
                  allowEmpty={false}
                  placeholder="Pick clock-in"
                />
              </label>
              <label>
                <span>Clock Out</span>
                <HopDateTimePicker
                  value={entryForm.out}
                  onChange={(v) => setEntryForm(f => ({ ...f, out: v }))}
                  allowEmpty
                  placeholder="Pick clock-out"
                />
                <small>Leave blank to keep "in progress"</small>
              </label>
            </div>
            {entryConflict && (
              <div className="entry-edit-conflict" role="alert">
                <strong>Overlaps an existing entry</strong>
                <div className="entry-edit-conflict-meta">
                  {fmtEntryDateRange(entryConflict.clock_in_time, entryConflict.clock_out_time)}
                  {' · '}
                  {fmtTime(entryConflict.clock_in_time)} → {entryConflict.clock_out_time
                    ? fmtTime(entryConflict.clock_out_time)
                    : 'in progress'}
                </div>
              </div>
            )}
            {entryErr && !entryConflict && <div className="admin-error">{entryErr}</div>}
            {/* Sprint 19.3 — delete lives inside Edit, behind a confirm. */}
            {entryMode === 'edit' && confirmEntryDelete && (
              <div className="entry-delete-confirm" role="alertdialog" aria-label="Confirm delete">
                <div className="entry-delete-confirm-text">
                  <strong>Delete this entry?</strong>
                  <span>
                    {fmtEntryDateRange(editEntry.clock_in_time, editEntry.clock_out_time)}
                    {' · '}{fmtTime(editEntry.clock_in_time)} → {editEntry.clock_out_time ? fmtTime(editEntry.clock_out_time) : 'in progress'}
                    {' — this removes it from payroll hours. It is recorded in the audit log.'}
                  </span>
                </div>
                <div className="entry-delete-confirm-btns">
                  <button type="button" className="btn-logout" onClick={() => setConfirmEntryDelete(false)} disabled={entryDeleting}>Keep</button>
                  <button type="button" className="btn-delete-confirm" onClick={deleteEntry} disabled={entryDeleting}>
                    {entryDeleting ? 'Deleting…' : 'Yes, delete'}
                  </button>
                </div>
              </div>
            )}
            <div className="entry-edit-actions">
              {entryMode === 'edit' && !confirmEntryDelete && (
                <button
                  type="button"
                  className="entry-delete-btn"
                  onClick={() => setConfirmEntryDelete(true)}
                  disabled={entrySave}
                >
                  Delete
                </button>
              )}
              <button type="button" className="btn-logout" onClick={closeEntryEdit}>Cancel</button>
              <button type="submit" className="btn-save" disabled={entrySave || entryDeleting}>
                {entrySave
                  ? 'Saving…'
                  : entryMode === 'create' ? 'Add entry' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default StaffDetail;
